import { Box, type SxProps, type Theme } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "./Tooltip";

interface TruncatedTextProps {
  /** The full text. Also what the tooltip shows when it doesn't fit. */
  text: string;
  /** Width at which the text ellipsises. Any CSS width value. */
  maxWidth: number | string;
  /** Extra styles for the text span (font, colour, …). */
  sx?: SxProps<Theme>;
  /** Tooltip side. */
  placement?: "top" | "bottom" | "left" | "right";
}

/**
 * One line of text clipped with an ellipsis, with a tooltip carrying the full
 * string — but **only when it is actually clipped**. A tooltip that repeats
 * text the reader can already see is noise.
 *
 * `inline-block` + `verticalAlign: bottom` so a clipped span keeps the same
 * baseline as text beside it (inline-block otherwise drops it).
 */
export const TruncatedText = ({
  text,
  maxWidth,
  sx,
  placement = "top",
}: TruncatedTextProps) => {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the text changes — a ResizeObserver only fires on size changes, and swapping the string doesn't resize the fixed-width span
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const check = () => setIsTruncated(el.scrollWidth - el.clientWidth > 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return (
    <Tooltip
      title={isTruncated ? text : ""}
      placement={placement}
      disableSentenceCase
    >
      <Box
        ref={textRef}
        component="span"
        sx={{
          display: "inline-block",
          verticalAlign: "bottom",
          minWidth: 0,
          // Caller styles land here — before the clipping rules, so passing
          // `whiteSpace` or `overflow` can't silently un-truncate the text.
          ...sx,
          maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
};
